// Cash Repairs: a driver manager asks for cash to fix one car, item by item;
// Luyanda, Meseum or Yolisa approve; Luyanda pays. db/migrations/3103.
//
// When a request is paid every item becomes a line on the servicing page in its
// own category, so a windscreen, two brake pads and a bulb paid in cash are seen,
// split by who pays and read by the invoice reader exactly as an invoice is.
// Every figure and every refusal is Postgres (CLAUDE.md rule 4); this page
// chooses words, never amounts or permissions.
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Hammer, Check, ChevronDown, ChevronRight, ExternalLink, FileText, Plus, ShieldAlert, Upload, X } from 'lucide-react'
import {
  useApproveCashRepair, useCancelCashRepair, useConfirmCashRepairSlip, useDeclineCashRepair, useGetCashRepairOptions,
  useGetCashRepairs, useMarkCashRepairPaid, useUploadCashRepairQuote, useUploadCashRepairReceipt,
} from '../hooks/backend/cashRepairs'
import { useGetMaintenanceInvoiceUrl } from '../hooks/backend/fleet'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { toast } from '../lib/shadcn/sonner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { RaiseCashRepairDialog } from '../components/cashRepairs/RaiseCashRepairDialog'
import { writeErrorMessage } from '../utils/writeError'
import { fileToBase64 } from '../utils/marketing'
import { BORNE_TONE, borneLabel, categoryLabel, zarExact } from '../utils/servicing'
import {
  CASH_REPAIR_STATUS_LABEL, CASH_REPAIR_STATUS_TONE, sast,
  type CashRepair, type CashRepairList, type CashRepairOptions,
} from '../utils/cashRepairs'

const FILTERS: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: '', label: 'All' },
  { value: 'requested', label: 'Waiting for approval' },
  { value: 'approved', label: 'Approved, to pay' },
  { value: 'paid', label: 'Paid' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
]

export default function CashRepairs() {
  const optionsQuery = useGetCashRepairOptions<CashRepairOptions | null>()
  const listQuery = useGetCashRepairs<CashRepairList | null>()
  const [status, setStatus] = useState('open')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [raising, setRaising] = useState(false)

  const loadOptions = optionsQuery.trigger
  const loadList = listQuery.trigger
  useEffect(() => { void loadOptions({}, { skipCache: true }).catch(() => undefined) }, [loadOptions])
  const refreshList = useCallback(() => {
    void loadList({ status: status || null }, { skipCache: true }).catch(() => undefined)
  }, [loadList, status])
  useEffect(() => { refreshList() }, [refreshList])

  const options = optionsQuery.data ?? null
  const access = options?.access ?? null
  const rows = listQuery.data?.rows ?? []
  const counts = listQuery.data?.counts

  if (optionsQuery.loading && !optionsQuery.data && !optionsQuery.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading…</div>
  }
  if (optionsQuery.error || (access && !access.can_view)) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm text-foreground">
            {optionsQuery.error ?? 'Cash repairs are open to the driver managers, Xolile, Luyanda, Meseum and Yolisa. Ask an admin to switch on Raise cash repairs for you in cockpit access.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Hammer className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Cash Repairs</h1>
          <div className="ml-auto flex items-center gap-2">
            {access?.can_raise && options && (
              <Button size="sm" className="h-8 gap-1.5 px-2.5 text-xs" onClick={() => setRaising(true)}>
                <Plus className="h-3.5 w-3.5" /> Raise a cash repair
              </Button>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Raised by the driver managers, Xolile or Luyanda; approved by {access?.approvers ?? 'an approver'}, never by the
          person who raised it; paid by {access?.payers ?? 'the payer'}. Each paid item lands on{' '}
          <Link to="/servicing" className="underline">Servicing and Repairs</Link> in its own category.
        </p>
        {counts && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip tone="amber">Waiting for approval <span className="ml-1 font-semibold tabular-nums">{counts.requested}, {zarExact(counts.requested_zar)}</span></Chip>
            <Chip tone="brand">Approved, to pay <span className="ml-1 font-semibold tabular-nums">{counts.approved}, {zarExact(counts.approved_zar)}</span></Chip>
            <Chip tone="green">Paid <span className="ml-1 font-semibold tabular-nums">{counts.paid}</span></Chip>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
        <NativeSelect className="h-8 w-[200px] text-xs" value={status} onChange={(e) => setStatus(e.target.value)}>
          {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </NativeSelect>
        <span className="text-xs text-muted-foreground">{listQuery.loading ? '…' : `${rows.length} request${rows.length === 1 ? '' : 's'}`}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {listQuery.error ? (
          <p className="m-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{listQuery.error}</p>
        ) : rows.length === 0 && !listQuery.loading ? (
          <p className="p-10 text-center text-sm text-muted-foreground">No cash repairs here.</p>
        ) : (
          <ul>
            {rows.map((r) => (
              <RequestRow key={r.id} row={r} open={expanded === r.id}
                onToggle={() => setExpanded((cur) => (cur === r.id ? null : r.id))}
                access={access} />
            ))}
          </ul>
        )}
      </div>

      {options && (
        <RaiseCashRepairDialog open={raising} onOpenChange={setRaising} options={options} onSaved={refreshList} />
      )}
    </div>
  )
}

function RequestRow({ row, open, onToggle, access }: {
  row: CashRepair; open: boolean; onToggle: () => void; access: CashRepairOptions['access'] | null
}) {
  const canDecide = Boolean(access?.can_approve) && row.status === 'requested'
  const canDecline = Boolean(access?.can_approve) && (row.status === 'requested' || row.status === 'approved')
  const canCancel = (row.is_mine || Boolean(access?.can_approve)) && (row.status === 'requested' || row.status === 'approved')
  const canPay = Boolean(access?.can_pay) && row.status === 'approved'

  return (
    <li className="border-b border-border">
      <button type="button" onClick={onToggle} className="flex w-full flex-wrap items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/40">
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <span className="font-mono text-xs font-semibold text-foreground">{row.ref}</span>
        <span className="font-mono text-xs font-semibold uppercase text-foreground">{row.registration_number}</span>
        <span className="min-w-[200px] flex-1 truncate text-xs text-muted-foreground">
          {row.items.map((i) => i.label).join(', ')}: {row.reason}
        </span>
        <span className="text-xs tabular-nums text-foreground">{zarExact(row.paid_zar ?? row.quoted_zar)}</span>
        <Chip tone={CASH_REPAIR_STATUS_TONE[row.status]}>{CASH_REPAIR_STATUS_LABEL[row.status]}</Chip>
        <span className="text-[11px] text-muted-foreground">{row.requested_by_name}, {sast(row.requested_at)}</span>
      </button>
      {open && (
        <div className="space-y-3 bg-muted/20 px-4 pb-4 pt-1">
          <div className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
            <div><span className="text-muted-foreground">Driver:</span> {row.driver_name ?? 'None named'}</div>
            <div><span className="text-muted-foreground">Raised:</span> {row.requested_by_name}, {sast(row.requested_at)}</div>
            {row.approved_at && <div><span className="text-muted-foreground">Approved:</span> {row.approved_by_name}, {sast(row.approved_at)}</div>}
            {row.paid_at && <div><span className="text-muted-foreground">Paid:</span> {row.paid_by_name}, {sast(row.paid_at)}{row.paid_note ? `, “${row.paid_note}”` : ''}</div>}
            {row.declined_at && <div className="text-rose-700 dark:text-rose-300">Declined by {row.declined_by_name}, {sast(row.declined_at)}: {row.decline_reason}</div>}
            {row.cancelled_at && <div className="text-muted-foreground">Cancelled {sast(row.cancelled_at)}: {row.cancel_reason}</div>}
          </div>
          {row.source === 'driver_shift_form' && (
            <DriverSlip row={row} canConfirm={(Boolean(access?.can_raise) || Boolean(access?.can_approve)) && row.status === 'requested' && !row.is_mine} />
          )}
          <Documents row={row} canAddQuote={(Boolean(access?.can_raise) || Boolean(access?.can_approve)) && (row.status === 'requested' || row.status === 'approved')} />
          {row.status === 'approved' && canPay ? (
            <PayPanel row={row} />
          ) : (
            <ItemsTable row={row} />
          )}
          <div className="flex flex-wrap items-center gap-2">
            {canDecide && !row.is_mine && <ApproveButton row={row} />}
            {canDecide && row.is_mine && (
              <span className="text-[11px] text-muted-foreground">You raised this, so another approver approves it.</span>
            )}
            {canDecline && <ReasonAction row={row} kind="decline" />}
            {canCancel && <ReasonAction row={row} kind="cancel" />}
            {row.status === 'paid' && <SlipUpload row={row} />}
            {row.status === 'paid' && (
              <Link to="/servicing" className="text-[11px] text-[hsl(var(--brand))] underline">See the lines on Servicing and Repairs</Link>
            )}
          </div>
        </div>
      )}
    </li>
  )
}

function ItemsTable({ row }: { row: CashRepair }) {
  return (
    <table className="w-full max-w-3xl text-xs">
      <thead>
        <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
          <th className="py-1 text-left font-semibold">Item</th>
          <th className="py-1 text-left font-semibold">Category</th>
          <th className="py-1 text-left font-semibold">Who pays</th>
          <th className="py-1 text-right font-semibold">Asked</th>
          <th className="py-1 text-right font-semibold">Paid</th>
        </tr>
      </thead>
      <tbody>
        {row.items.map((i) => (
          <tr key={i.id} className="border-b border-border/60">
            <td className="py-1 text-foreground">{i.label}{i.description ? `, ${i.description}` : ''}</td>
            <td className="py-1">{categoryLabel(i.category)}</td>
            <td className="py-1">{i.borne_by ? <Chip tone={BORNE_TONE[i.borne_by]}>{borneLabel(i.borne_by)}</Chip> : '—'}</td>
            <td className="py-1 text-right tabular-nums">{zarExact(i.quoted_zar)}</td>
            <td className="py-1 text-right tabular-nums">
              {i.paid_zar === null ? '—' : zarExact(i.paid_zar)}
              {i.vat_zar > 0 && <span className="ml-1 text-[10px] text-muted-foreground">incl {zarExact(i.vat_zar)} VAT</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * A repair the driver already paid for, reported on the website shift form.
 * The slip is approved only when the reader matched it or a manager confirmed
 * its total by eye (5703, 5704), so this says which, and offers the confirm.
 */
function DriverSlip({ row, canConfirm }: { row: CashRepair; canConfirm: boolean }) {
  const confirm = useConfirmCashRepairSlip<{ amount_zar: number; changed_from_zar: number | null }>()
  const [amount, setAmount] = useState('')
  const matched = row.slip_check?.verdict === 'match'
  const confirmed = Boolean(row.slip_confirmed_at)
  const from = row.paid_from === 'uber_cash' ? 'Uber cash the driver collected' : 'the driver’s own cash'
  return (
    <div className="max-w-3xl space-y-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs">
      <div className="text-foreground">
        Reported by the driver on the shift form, paid from {from}.
        {row.paid_from === 'uber_cash'
          ? ' Approving it counts the amount against the Uber cash the driver holds; no cash leaves.'
          : ' Approving it means Fleetly owes the driver this amount; mark it paid once they have it back.'}
      </div>
      {confirmed ? (
        <div className="text-emerald-700 dark:text-emerald-300">
          Slip total confirmed by eye by {row.slip_confirmed_by_name ?? 'a manager'}, {sast(row.slip_confirmed_at!)}: {zarExact(row.slip_confirmed_zar ?? 0)}
          {row.driver_typed_zar !== null && row.driver_typed_zar !== undefined && ` (the driver typed ${zarExact(row.driver_typed_zar)})`}.
        </div>
      ) : matched ? (
        <div className="text-emerald-700 dark:text-emerald-300">Slip checked: it matches the amount the driver typed.</div>
      ) : (
        <div className="text-amber-700 dark:text-amber-300">
          The slip did not check out: {row.slip_check?.message ?? 'there is no slip on record.'} Open the slip below. If it is right, confirm its total; if not, decline.
        </div>
      )}
      {canConfirm && !matched && !confirmed && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">Total on the slip</span>
          <Input className="h-8 w-[120px] text-xs tabular-nums" inputMode="decimal" placeholder="85.00" value={amount}
            onChange={(e) => setAmount(e.target.value)} />
          <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={confirm.loading || !amount.trim()} onClick={async () => {
            try {
              const r = await confirm.trigger({ id: row.id, amount_zar: amount }).result
              toast.success(`Slip on ${row.ref} confirmed`, {
                description: r.changed_from_zar !== null && r.changed_from_zar !== undefined
                  ? `The request now carries ${zarExact(r.amount_zar)} from the slip; the driver typed ${zarExact(r.changed_from_zar)}.`
                  : 'It can be approved now.',
              })
              setAmount('')
            } catch (err) {
              toast.error('Could not confirm the slip', { description: writeErrorMessage(err) })
            }
          }}>
            {confirm.loading ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />} Confirm by eye
          </Button>
        </div>
      )}
    </div>
  )
}

function ApproveButton({ row }: { row: CashRepair }) {
  const approve = useApproveCashRepair()
  return (
    <Button size="sm" className="h-8 gap-1 text-xs" disabled={approve.loading} onClick={async () => {
      try {
        await approve.trigger({ id: row.id }).result
        toast.success(`${row.ref} approved`)
      } catch (err) {
        toast.error('Could not approve', { description: writeErrorMessage(err) })
      }
    }}>
      {approve.loading ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />} Approve {zarExact(row.quoted_zar)}
    </Button>
  )
}

function ReasonAction({ row, kind }: { row: CashRepair; kind: 'decline' | 'cancel' }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const decline = useDeclineCashRepair()
  const cancel = useCancelCashRepair()
  const busy = decline.loading || cancel.loading
  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setOpen(true)}>
        <X className="h-3.5 w-3.5" /> {kind === 'decline' ? 'Decline' : 'Cancel request'}
      </Button>
    )
  }
  return (
    <div className="flex items-center gap-2">
      <Input className="h-8 w-[280px] text-xs" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
        placeholder={kind === 'decline' ? 'Get a second quote first; R1,800 is high for this' : 'Fixed under the owner’s warranty instead'} />
      <Button size="sm" variant="destructive" className="h-8 text-xs" disabled={busy} onClick={async () => {
        try {
          if (kind === 'decline') await decline.trigger({ id: row.id, reason }).result
          else await cancel.trigger({ id: row.id, reason }).result
          toast.success(`${row.ref} ${kind === 'decline' ? 'declined' : 'cancelled'}`)
          setOpen(false)
        } catch (err) {
          toast.error(`Could not ${kind}`, { description: writeErrorMessage(err) })
        }
      }}>
        {busy && <Spinner className="mr-1 h-3.5 w-3.5" />} {kind === 'decline' ? 'Decline' : 'Cancel it'}
      </Button>
      <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setOpen(false)}>Back</Button>
    </div>
  )
}

/** The payer can record what actually left the hand, item by item. */
function PayPanel({ row }: { row: CashRepair }) {
  const [paid, setPaid] = useState<Record<string, string>>(() => Object.fromEntries(row.items.map((i) => [i.id, String(i.quoted_zar)])))
  const [vat, setVat] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')
  const pay = useMarkCashRepairPaid<{ paid_zar: number }>()
  const total = row.items.reduce((s, i) => s + (Number(paid[i.id]) || 0), 0)

  return (
    <div className="space-y-2">
      <table className="w-full max-w-3xl text-xs">
        <thead>
          <tr className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className="py-1 text-left font-semibold">Item</th>
            <th className="py-1 text-left font-semibold">Category</th>
            <th className="py-1 text-left font-semibold">Who pays</th>
            <th className="py-1 text-right font-semibold">Asked</th>
            <th className="py-1 text-right font-semibold">Paid in cash</th>
            <th className="py-1 text-right font-semibold">VAT on the slip</th>
          </tr>
        </thead>
        <tbody>
          {row.items.map((i) => (
            <tr key={i.id} className="border-b border-border/60">
              <td className="py-1">{i.label}{i.description ? `, ${i.description}` : ''}</td>
              <td className="py-1">{categoryLabel(i.category)}</td>
              <td className="py-1">{i.borne_by ? <Chip tone={BORNE_TONE[i.borne_by]}>{borneLabel(i.borne_by)}</Chip> : '—'}</td>
              <td className="py-1 text-right tabular-nums">{zarExact(i.quoted_zar)}</td>
              <td className="py-1 text-right">
                <Input className="ml-auto h-7 w-[110px] text-right text-xs" type="number" inputMode="decimal"
                  value={paid[i.id] ?? ''} onChange={(e) => setPaid((p) => ({ ...p, [i.id]: e.target.value }))} />
              </td>
              <td className="py-1 text-right">
                <Input className="ml-auto h-7 w-[100px] text-right text-xs" type="number" inputMode="decimal" placeholder="0"
                  value={vat[i.id] ?? ''} onChange={(e) => setVat((p) => ({ ...p, [i.id]: e.target.value }))} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-8 w-[300px] text-xs" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Paid to the fitter at the rank, slip photographed" />
        <Button size="sm" className="h-8 text-xs" disabled={pay.loading} onClick={async () => {
          try {
            const r = await pay.trigger({
              id: row.id, note,
              items: row.items.map((i) => ({ id: i.id, paid_zar: paid[i.id] ?? null, vat_zar: vat[i.id] ?? null })),
            }).result
            toast.success(`${row.ref} paid, ${zarExact(r.paid_zar)}`, { description: 'Each item is now a line on Servicing and Repairs. File the slip next.' })
          } catch (err) {
            toast.error('Could not mark it paid', { description: writeErrorMessage(err) })
          }
        }}>
          {pay.loading && <Spinner className="mr-1 h-3.5 w-3.5" />} Mark paid {zarExact(total)}
        </Button>
        <span className="text-[11px] text-muted-foreground">VAT is zero unless the slip shows it; most cash fitters are not VAT registered.</span>
      </div>
    </div>
  )
}

function SlipUpload({ row }: { row: CashRepair }) {
  const ref = useRef<HTMLInputElement>(null)
  const upload = useUploadCashRepairReceipt<{ error?: string }>()
  return (
    <div className="flex items-center gap-2">

      <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0]
          if (!f) return
          try {
            const contentBase64 = await fileToBase64(f)
            const r = await upload.trigger({ id: row.id, filename: f.name, contentType: f.type, contentBase64 }).result
            if (r?.error) toast.warning('Slip filed, but not read', { description: r.error })
            else toast.success('Slip filed and read', { description: 'Its suggestions show against the lines on Servicing and Repairs.' })
          } catch (err) {
            toast.error('Could not file the slip', { description: writeErrorMessage(err) })
          } finally {
            if (ref.current) ref.current.value = ''
          }
        }} />
      <Button size="sm" variant="outline" className={cn('h-8 gap-1 text-xs')} disabled={upload.loading} onClick={() => ref.current?.click()}>
        {upload.loading ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />} File the slip
      </Button>
    </div>
  )
}

/**
 * The quote the amounts came from and the slip for what was paid, side by side
 * with what was asked, so an approver reads the document and not only the
 * number typed from it.
 */
function Documents({ row, canAddQuote }: { row: CashRepair; canAddQuote: boolean }) {
  const url = useGetMaintenanceInvoiceUrl<{ url: string }>()
  const upload = useUploadCashRepairQuote<{ read_status: string; read_error: string | null; total_zar: number | string | null }>()
  const ref = useRef<HTMLInputElement>(null)
  const quotes = row.receipts.filter((d) => d.source === 'cash_quote')
  const slips = row.receipts.filter((d) => d.source !== 'cash_quote')

  async function open(id: string) {
    try {
      const r = await url.trigger({ invoice_id: id }, { skipCache: true }).result
      window.open(r.url, '_blank', 'noopener')
    } catch (err) {
      toast.error('Could not open the file', { description: writeErrorMessage(err) })
    }
  }

  if (quotes.length === 0 && slips.length === 0 && !canAddQuote) return null
  return (
    <div className="flex max-w-3xl flex-wrap items-center gap-2 text-xs">
      {quotes.length === 0 && (
        <span className="text-muted-foreground">No quote on this request.</span>
      )}
      {[...quotes, ...slips].map((d) => {
        const total = d.total_zar === null || d.total_zar === undefined ? null : Number(d.total_zar)
        const differs = d.source === 'cash_quote' && total !== null && Math.abs(total - Number(row.quoted_zar)) >= 0.01
        return (
          <span key={d.id} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1">
            <FileText className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-foreground">{d.source === 'cash_quote' ? 'Quote' : 'Slip'}</span>
            <span className="text-muted-foreground">{d.supplier_name ?? d.filename ?? ''}</span>
            {total !== null && <span className="font-semibold tabular-nums text-foreground">{zarExact(total)}</span>}
            {differs && <span className="text-amber-700 dark:text-amber-300">asked {zarExact(row.quoted_zar)}</span>}
            {d.read_status !== 'read' && <span className="text-rose-700 dark:text-rose-300" title={d.read_error ?? ''}>not read</span>}
            <button type="button" className="ml-1 inline-flex items-center gap-0.5 text-[hsl(var(--brand))] hover:underline" onClick={() => open(d.id)}>
              <ExternalLink className="h-3 w-3" /> Open
            </button>
          </span>
        )
      })}
      {canAddQuote && (
        <>
          <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0]
              if (!f) return
              try {
                const contentBase64 = await fileToBase64(f)
                const r = await upload.trigger({ id: row.id, filename: f.name, contentType: f.type, contentBase64 }).result
                if (r.read_status !== 'read') toast.warning('Quote attached, but not read', { description: r.read_error ?? undefined })
                else toast.success(`Quote attached to ${row.ref}`)
              } catch (err) {
                toast.error('Could not attach the quote', { description: writeErrorMessage(err) })
              } finally {
                if (ref.current) ref.current.value = ''
              }
            }} />
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={upload.loading} onClick={() => ref.current?.click()}>
            {upload.loading ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />} {quotes.length ? 'Add another quote' : 'Attach a quote'}
          </Button>
        </>
      )}
    </div>
  )
}
